import { Block, EmptyState, Freshness, LoadingBlock, Metric, MetricGrid, Panel, SectionHeader, SourceNote } from "../components/ui";
import { Chart, type ChartDatum } from "../components/chart";
import { DataTable } from "../components/table";
import { compactNum, num, shortDay, toMillis } from "../lib/format";

// Types mirrored from packages/functions/src/scheduled/seo-feedback-loop.ts
export interface SeoGscRow {
  keys: string[];
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface SeoSurfaceStats {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  pages: number;
}

export interface SeoRunDoc {
  generatedAt?: { seconds: number; nanoseconds?: number } | null;
  period?: { start: string; end: string; days: number };
  totals: { clicks: number; impressions: number; ctr: number; position: number };
  surfaces: Record<string, SeoSurfaceStats>;
  topPages: SeoGscRow[];
  topQueries: SeoGscRow[];
  trend?: SeoGscRow[];
  sitemaps: { path: string; errors: number; warnings: number; indexedUrls: number | null }[];
  inspect: { url: string; coverage?: string; verdict?: string; rich?: string; error?: string }[];
  health: { pseoUrls: number; maxLastmod: string };
  deltas: { impressions: number | null; clicks: number | null };
  actions: string[];
}

interface Props {
  /** null while the listener has not answered; [] when the collection is empty. */
  runs: SeoRunDoc[] | null;
  listenerFailed?: boolean;
}

function ctr(n: number | null | undefined): string {
  return n != null ? `${(n * 100).toFixed(2)}%` : "—";
}

function position(n: number | null | undefined): string {
  return n == null || n === 0 ? "—" : n.toFixed(1);
}

function runMillis(run: SeoRunDoc): number | null {
  return toMillis(run.generatedAt) ?? toMillis(run.period?.end ?? null);
}

type SurfaceRow = SeoSurfaceStats & { name: string };

export function SeoSection({ runs, listenerFailed = false }: Props) {
  const runList = runs ?? [];
  const latest = runList[0] ?? null;

  const asc: ChartDatum[] = [...runList].reverse().map((r, i) => ({
    x: `${runMillis(r) ?? i}`,
    xLabel: shortDay(runMillis(r)),
    values: { impressions: r.totals.impressions, clicks: r.totals.clicks ?? null, position: r.totals.position || null },
  }));

  const daily: ChartDatum[] = (latest?.trend ?? [])
    .filter((r) => typeof r.keys?.[0] === "string" && r.keys[0].length > 0)
    .sort((a, b) => a.keys[0]!.localeCompare(b.keys[0]!))
    .map((r) => ({ x: r.keys[0]!, xLabel: shortDay(r.keys[0]), values: { clicks: r.clicks ?? 0 } }));

  const surfaces: SurfaceRow[] = latest ? Object.entries(latest.surfaces).map(([name, s]) => ({ name, ...s })) : [];

  return (
    <div>
      <SectionHeader
        title="SEO"
        subtitle={
          latest
            ? `Daily loop (07:00 UTC) · period ${latest.period?.start ?? "?"} → ${latest.period?.end ?? "?"} · pSEO ${num(latest.health.pseoUrls)} URLs`
            : "Daily SEO loop (Search Console + pSEO health), written at 07:00 UTC to _admin/seoReports/runs."
        }
        meta={<Freshness at={latest ? runMillis(latest) : null} failed={listenerFailed} source="_admin/seoReports/runs" staleAfterMs={26 * 3_600_000} />}
      />

      {runs === null && <LoadingBlock metrics={4} rows={3} label="Loading SEO reports" />}
      {runs !== null && runs.length === 0 && <EmptyState message="No reports yet — the first daily run lands at 07:00 UTC." />}

      {latest && (
        <>
          <MetricGrid cols={4} className="mb-8">
            <Metric
              label="Impressions (28d)"
              value={num(latest.totals.impressions)}
              delta={{ value: latest.deltas.impressions, caption: "vs previous run" }}
            />
            <Metric label="Clicks (28d)" value={num(latest.totals.clicks)} delta={{ value: latest.deltas.clicks, caption: "vs previous run" }} />
            <Metric
              label="CTR"
              value={ctr(latest.totals.ctr)}
              tone={latest.totals.ctr < 0.001 ? "warn" : "default"}
              sub={latest.totals.ctr < 0.001 ? "Low — review titles and meta descriptions" : undefined}
            />
            <Metric
              label="Average position"
              value={position(latest.totals.position)}
              sub={latest.totals.position > 0 && latest.totals.position <= 15 ? "In the top 15" : "Outside the top 15"}
            />
          </MetricGrid>

          {latest.actions.length > 0 && (
            <Block title="Suggested actions">
              <Panel>
                <ul className="list-disc space-y-1 pl-5 text-label">
                  {latest.actions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </Panel>
            </Block>
          )}

          {/* One small chart per measure, same X (admin-04): no dual axes. */}
          <Block title="Progress by daily run">
            <div className="grid grid-cols-1 gap-3 @4xl:grid-cols-3">
              <Panel>
                <p className="mb-2 text-caption text-muted-foreground">Impressions (28d)</p>
                <Chart
                  title="Impressions per run"
                  data={asc}
                  series={[{ key: "impressions", label: "Impressions", color: "var(--chart-1)", kind: "line" }]}
                  format={num}
                  formatAxis={compactNum}
                  height={140}
                />
              </Panel>
              <Panel>
                <p className="mb-2 text-caption text-muted-foreground">Clicks (28d)</p>
                <Chart
                  title="Clicks per run"
                  data={asc}
                  series={[{ key: "clicks", label: "Clicks", color: "var(--chart-2)", kind: "line" }]}
                  format={num}
                  formatAxis={compactNum}
                  height={140}
                />
              </Panel>
              <Panel>
                <p className="mb-2 text-caption text-muted-foreground">Average position (lower is better)</p>
                <Chart
                  title="Average position per run"
                  data={asc}
                  series={[{ key: "position", label: "Position", color: "var(--chart-3)", kind: "line" }]}
                  format={(v) => v.toFixed(1)}
                  formatAxis={(v) => v.toFixed(0)}
                  height={140}
                  invert
                  domain={[0, 30]}
                />
              </Panel>
            </div>
          </Block>

          {daily.length > 0 && (
            <Block title="Daily clicks · last 14 days (Search Console)">
              <Panel>
                <Chart
                  title="Daily clicks"
                  data={daily}
                  series={[{ key: "clicks", label: "Clicks", color: "var(--chart-2)", kind: "bar" }]}
                  format={num}
                  formatAxis={compactNum}
                  height={140}
                  showLastValue={false}
                />
              </Panel>
            </Block>
          )}

          <div className="mb-8 grid grid-cols-1 gap-6 @4xl:grid-cols-2">
            <Block title="pSEO surfaces (28d)" className="mb-0">
              <DataTable
                caption="pSEO surfaces"
                rows={surfaces}
                rowKey={(s) => s.name}
                defaultSort={{ key: "imp", dir: "desc" }}
                columns={[
                  { key: "name", header: "Surface", cell: (s) => <span className="font-mono">{s.name}</span>, sortValue: (s) => s.name },
                  { key: "pages", header: "Pages", align: "right", cell: (s) => num(s.pages), sortValue: (s) => s.pages },
                  { key: "imp", header: "Impressions", align: "right", cell: (s) => num(s.impressions), sortValue: (s) => s.impressions },
                  { key: "clicks", header: "Clicks", align: "right", cell: (s) => num(s.clicks), sortValue: (s) => s.clicks },
                  { key: "ctr", header: "CTR", align: "right", cell: (s) => ctr(s.ctr), sortValue: (s) => s.ctr },
                  { key: "pos", header: "Position", align: "right", cell: (s) => position(s.position), sortValue: (s) => s.position || null },
                ]}
              />
            </Block>
            <Block title="Run history" className="mb-0">
              <DataTable
                caption="SEO run history"
                rows={runList.slice(0, 14)}
                rowKey={(r, i) => `${runMillis(r) ?? i}`}
                columns={[
                  { key: "date", header: "Date", cell: (r) => shortDay(runMillis(r)) },
                  { key: "imp", header: "Impressions", align: "right", cell: (r) => num(r.totals.impressions) },
                  { key: "clicks", header: "Clicks", align: "right", cell: (r) => num(r.totals.clicks) },
                  { key: "ctr", header: "CTR", align: "right", cell: (r) => ctr(r.totals.ctr) },
                  { key: "pos", header: "Position", align: "right", cell: (r) => position(r.totals.position) },
                ]}
              />
            </Block>
          </div>

          <Block title="Sitemaps and pSEO health">
            <DataTable
              caption="Sitemaps"
              rows={[
                ...latest.sitemaps.map((s) => ({ key: s.path, label: s.path, errors: s.errors, warnings: s.warnings, indexed: s.indexedUrls })),
              ]}
              rowKey={(r) => r.key}
              columns={[
                { key: "path", header: "Sitemap", cell: (r) => <span className="font-mono">{r.label}</span> },
                {
                  key: "errors",
                  header: "Errors",
                  align: "right",
                  cell: (r) => <span className={r.errors > 0 ? "text-danger-fg" : undefined}>{num(r.errors)}</span>,
                },
                {
                  key: "warnings",
                  header: "Warnings",
                  align: "right",
                  cell: (r) => <span className={r.warnings > 0 ? "text-warning-fg" : undefined}>{num(r.warnings)}</span>,
                },
                { key: "indexed", header: "Indexed", align: "right", cell: (r) => num(r.indexed) },
              ]}
            />
            <SourceNote>
              Production pSEO: {num(latest.health.pseoUrls)} URLs · latest lastmod {latest.health.maxLastmod || "—"}
            </SourceNote>
          </Block>
        </>
      )}
    </div>
  );
}
